// The `evidence/library` COLLECTIONS api module — carved from
// `src/components/library/api.ts` plus the one residue the source-list carve
// had to leave behind.
//
// THIS IS THE FILE THE FAMILY RECORD HAS BEEN POINTING AT SINCE THE
// SOURCE-LIST API CARVE. That slice moved six `/source-lists` fns and
// recorded `importSourceList` as
// an exclusion by ROUTE: it POSTs to
// `/evidence/collections:import-source-list/{id}` and returns
// `CollectionDetailResponse`, "a `/evidence/collections` route and the head of
// a ~100-line collections type chain that no package owns". The exclusion's own
// heal path was "it lands with the PR that opens collections". The collections
// route family is what this module is, so the seventh fn comes home here —
// beside the resource it was always part of — rather than beside the six it was
// filed with.
//
// TRANSPORT. The pre-move module imported the `@/utils/scribeHTTP` singleton
// directly; this one reads the SAME `scribeHttpV2` instance out of the
// `@heidi/api/client` registry, exactly as `data/api/sourceLists.ts` does. Same
// interceptors, same auth/refresh state, so every request is byte-identical:
// same paths, same verbs, same bodies, same `.data` unwrapping.
//
// `validateSourceListId` IS IMPORTED, NOT RE-DECLARED. The app-side residue
// already shared the package's one copy of that security check, and the whole
// point of the exclusion row was that it must not grow a second one.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  AddDocumentsRequest,
  CollectionCreateRequest,
  CollectionDetailResponse,
  CollectionListResponse,
  CollectionResponse,
  CollectionUpdateRequest,
} from '../../contracts';
import { validateSourceListId } from '../api/sourceLists';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

// `scribeHttpV2` base is /api/v2/ml-scribe/; scribe JWT injected by interceptor.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

const BASE_PATH = '/evidence/collections';

/**
 * Create a new collection
 */
export async function createCollection(
  data: CollectionCreateRequest
): Promise<CollectionResponse> {
  const response = await http().post<CollectionResponse>(BASE_PATH, data);
  return response.data;
}

/**
 * List all collections accessible to the current user
 */
export async function listCollections(): Promise<CollectionListResponse> {
  const response = await http().get<CollectionListResponse>(BASE_PATH);
  return response.data;
}

/**
 * Get a collection by ID with full details including document_ids
 */
export async function getCollection(
  collectionId: string
): Promise<CollectionDetailResponse> {
  const response = await http().get<CollectionDetailResponse>(
    `${BASE_PATH}/${collectionId}`
  );
  return response.data;
}

/**
 * Update a collection's name and/or description
 */
export async function updateCollection(
  collectionId: string,
  data: CollectionUpdateRequest
): Promise<CollectionResponse> {
  const response = await http().patch<CollectionResponse>(
    `${BASE_PATH}/${collectionId}`,
    data
  );
  return response.data;
}

/**
 * Delete a collection
 */
export async function deleteCollection(collectionId: string): Promise<void> {
  await http().delete(`${BASE_PATH}/${collectionId}`);
}

/**
 * Add documents to a collection
 */
export async function addDocumentsToCollection(
  collectionId: string,
  documentIds: string[]
): Promise<CollectionResponse> {
  const data: AddDocumentsRequest = { document_ids: documentIds };
  const response = await http().post<CollectionResponse>(
    `${BASE_PATH}/${collectionId}/documents`,
    data
  );
  return response.data;
}

/**
 * Remove a document from a collection
 */
export async function removeDocumentFromCollection(
  collectionId: string,
  documentId: string
): Promise<void> {
  await http().delete(`${BASE_PATH}/${collectionId}/documents/${documentId}`);
}

/**
 * Silently migrate a legacy source list into a collection.
 * Idempotent — safe to call multiple times, returns the same collection.
 * Returns 404 if source list not found or user has no access.
 */
export async function importSourceList(
  sourceListId: string
): Promise<CollectionDetailResponse> {
  const validatedId = validateSourceListId(sourceListId);
  const response = await http().post<CollectionDetailResponse>(
    `${BASE_PATH}:import-source-list/${validatedId}`
  );
  return response.data;
}
